import { useClubApi } from "../../lib/clubScope";
import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";

import { ApiError, type ClubToday, type ClubTodayCourt, type PlayerClubApplication } from "../../lib/api";
import { formatDate } from "../../lib/dateFormat";
import { sportLabel } from "../../lib/labels";
import ClubT123MembershipTile from "./ClubT123MembershipTile";
import {
  PX_PER_MIN,
  PX_PER_MIN_COMPACT,
  courtBlocks,
  courtOccupancy,
  dateFromIso,
  dayOptions,
  dayWindow,
  fromMinutes,
  isPastBlock,
  shortPeopleName,
  type DayBlock,
} from "./clubDayLayout";

export type ClubPanelTab = "hoy" | "reservas" | "socios" | "canchas" | "config";

export function localTodayIso(now: Date = new Date()): string {
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, "0");
  const d = String(now.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

const LONG_DATE = new Intl.DateTimeFormat("es-MX", { weekday: "long", day: "numeric", month: "long" });
const SHORT_DATE = new Intl.DateTimeFormat("es-MX", { weekday: "long", day: "numeric" });

function capitalize(text: string): string {
  return text.charAt(0).toUpperCase() + text.slice(1);
}

function requesterName(request: PlayerClubApplication): string {
  const p = request.player;
  return p.display_name || [p.first_name, p.last_name].filter(Boolean).join(" ") || "Jugador";
}

function sportClass(sport: string | null | undefined): string {
  if (sport === "padel") return "club-day__sport--padel";
  if (sport === "pickleball") return "club-day__sport--pickle";
  return "club-day__sport--tenis";
}

function blockTitle(block: DayBlock): string {
  if (block.kind === "tournament") return `🏆 ${block.detail ?? "Torneo"}`;
  if (block.kind === "blocked") return `⛔ ${block.label ?? "Bloqueo"}`;
  return block.label ?? "Reserva";
}

function blockSubtitle(block: DayBlock): string {
  if (block.kind === "tournament") return "Partido de torneo";
  if (block.kind === "blocked") return "Bloqueada por el club";
  return block.detail ?? "";
}

function nowMinutes(): number {
  const d = new Date();
  return d.getHours() * 60 + d.getMinutes();
}

interface Props {
  token: string;
  clubId: number;
  clubName: string;
  requests: PlayerClubApplication[];
  saving: boolean;
  reloadKey: number;
  onGoTo: (tab: ClubPanelTab) => void;
  onMembershipDecision: (requestId: number, action: "approve" | "reject") => void;
}

export default function ClubTodayPanel({
  token,
  clubId,
  clubName,
  requests,
  saving,
  reloadKey,
  onGoTo,
  onMembershipDecision,
}: Props) {
  const clubApi = useClubApi();
  const todayIso = localTodayIso();
  const [dateIso, setDateIso] = useState(todayIso);
  const [day, setDay] = useState<ClubToday | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [sportFilter, setSportFilter] = useState("");
  const [courtFilter, setCourtFilter] = useState("");
  const [mobileView, setMobileView] = useState<"court" | "all">("court");
  const [mobileCourtId, setMobileCourtId] = useState<number | null>(null);
  const [nowMin, setNowMin] = useState(nowMinutes);
  const datePickerRef = useRef<HTMLInputElement | null>(null);

  // Día que se está pidiendo: la fecha en pantalla cambia SOLO cuando llegan
  // sus datos; si falla, se queda el día anterior con su agenda.
  const [pendingDate, setPendingDate] = useState<string | null>(null);
  const dateRef = useRef(dateIso);
  dateRef.current = dateIso;
  const requestSeq = useRef(0);

  // Recarga (al montar o tras una acción) del día que ya está en pantalla.
  useEffect(() => {
    let alive = true;
    const date = dateRef.current;
    clubApi
      .today(token, date)
      .then((resp) => {
        if (alive && dateRef.current === date) {
          setDay(resp.data);
          setError(null);
        }
      })
      .catch((err) => {
        if (alive) setError(err instanceof ApiError ? err.message : "No pudimos cargar las canchas de ese día.");
      });
    return () => {
      alive = false;
    };
  }, [token, reloadKey]);

  async function selectDate(next: string) {
    if (next === dateIso || !/^\d{4}-\d{2}-\d{2}$/.test(next)) return;
    const seq = ++requestSeq.current;
    setPendingDate(next);
    try {
      const resp = await clubApi.today(token, next);
      if (seq !== requestSeq.current) return;
      setDay(resp.data);
      setDateIso(next);
      setError(null);
    } catch (err) {
      if (seq !== requestSeq.current) return;
      setError(
        `${err instanceof ApiError && err.message ? err.message : "No pudimos cargar ese día"}. Sigues viendo ${capitalize(LONG_DATE.format(dateFromIso(dateIso)))}.`,
      );
    } finally {
      if (seq === requestSeq.current) setPendingDate(null);
    }
  }

  useEffect(() => {
    const id = window.setInterval(() => setNowMin(nowMinutes()), 60_000);
    return () => window.clearInterval(id);
  }, []);

  // Una sola fuente para «por aprobar»: la misma lista que muestra Socios.
  const pendingRequests = requests.filter((r) => r.status === "pending_review");
  const isToday = dateIso === todayIso;
  const options = useMemo(() => dayOptions(todayIso), [todayIso]);
  const isOtherDay = !options.some((o) => o.iso === dateIso);

  const openCourts = useMemo(() => (day?.courts ?? []).filter((c) => c.open && c.slots.length > 0), [day]);
  const sports = useMemo(
    () =>
      Array.from(new Set(openCourts.map((c) => c.sport || "tenis"))).sort((a, b) =>
        sportLabel(a).localeCompare(sportLabel(b), "es", { sensitivity: "base" }),
      ),
    [openCourts],
  );
  const courtsBySport = openCourts.filter((c) => !sportFilter || (c.sport || "tenis") === sportFilter);
  const courtOptions = [...courtsBySport].sort((a, b) => a.name.localeCompare(b.name, "es", { numeric: true }));
  const visibleCourts = courtsBySport.filter((c) => !courtFilter || String(c.id) === courtFilter);
  const window_ = dayWindow(visibleCourts);
  const mobileCourt: ClubTodayCourt | undefined =
    openCourts.find((c) => c.id === mobileCourtId) ?? openCourts[0];

  const occupancy = day && day.slots_today > 0 ? Math.round((day.slots_taken / day.slots_today) * 100) : 0;
  const byStatus = day?.members_by_status ?? {};
  const suspended = byStatus.suspended ?? 0;
  const dateTitle = capitalize(LONG_DATE.format(dateFromIso(dateIso)));
  const dayWord = isToday ? "Hoy" : capitalize(SHORT_DATE.format(dateFromIso(dateIso)));

  function reserveHref(court: ClubTodayCourt, start: string): string {
    const params = new URLSearchParams({
      modo: "reservar",
      club: String(clubId),
      fecha: dateIso,
      cancha: court.name,
      hora: start,
    });
    return `/admin/calendar?${params.toString()}`;
  }

  function viewHref(): string {
    return `/admin/calendar?${new URLSearchParams({ modo: "reservar", club: String(clubId), fecha: dateIso }).toString()}`;
  }

  function openOtherDay() {
    const input = datePickerRef.current;
    if (!input) return;
    try {
      input.showPicker();
    } catch {
      input.focus();
      input.click();
    }
  }

  function renderBlockBody(block: DayBlock, compactFree = false, short = false) {
    if (block.kind === "free") {
      return compactFree ? null : (
        <>
          <span className="club-day__free-idle">{block.timeLabel}</span>
          <span className="club-day__free-hover">+ Reservar {block.timeLabel}</span>
        </>
      );
    }
    return (
      <>
        <small className="club-day__block-time">{block.timeLabel}</small>
        <strong className="club-day__block-name">
          {short && (block.kind === "reserved" || block.kind === "pending")
            ? shortPeopleName(blockTitle(block))
            : blockTitle(block)}
        </strong>
        {!short && <span className="club-day__block-detail">{blockSubtitle(block)}</span>}
      </>
    );
  }

  const nowLine =
    isToday && window_ && nowMin >= window_.startMin && nowMin <= window_.endMin
      ? (nowMin - window_.startMin) * PX_PER_MIN
      : null;

  const dayChips = (
    <div className="club-day__days" role="group" aria-label="Elegir día">
      {options.map((o) => (
        <button
          key={o.iso}
          type="button"
          className="club-day__chip"
          aria-pressed={dateIso === o.iso}
          disabled={pendingDate !== null}
          onClick={() => void selectDate(o.iso)}
        >
          <small>{o.top}</small>
          <strong>{o.bottom}</strong>
        </button>
      ))}
      <span className="club-day__cal-wrap">
        <button
          type="button"
          className="club-day__cal"
          aria-pressed={isOtherDay}
          disabled={pendingDate !== null}
          onClick={openOtherDay}
        >
          {isOtherDay ? `📅 ${dateFromIso(dateIso).getDate()}/${dateFromIso(dateIso).getMonth() + 1}` : "📅 Otro día"}
        </button>
        <input
          ref={datePickerRef}
          type="date"
          className="club-day__cal-input"
          aria-label="Otro día"
          tabIndex={-1}
          value={dateIso}
          onChange={(e) => void selectDate(e.target.value)}
        />
      </span>
    </div>
  );

  const legend = (
    <div className="club-day__legend" aria-label="Leyenda">
      <span><i className="club-day__sw club-day__sw--free" />Libre</span>
      <span><i className="club-day__sw club-day__sw--reserved" />Reservada</span>
      <span><i className="club-day__sw club-day__sw--tournament" />Torneo</span>
      <span><i className="club-day__sw club-day__sw--blocked" />Bloqueada</span>
    </div>
  );

  return (
    <>
      <section className="club-panel__hero" aria-label="Resumen del día">
        <div className="club-panel__hero-top">
          <div>
            <p className="club-panel__eyebrow">{clubName}</p>
            <h1>{isToday ? "Hoy en el club" : "Agenda del club"}</h1>
            <p>{dateTitle}</p>
          </div>
          <Link className="club-panel__lime-btn" to={viewHref()}>
            + Nueva reserva
          </Link>
        </div>
        <div className="club-panel__tiles club-panel__tiles--three">
          <div className="club-panel__tile">
            <span>{isToday ? "Reservas hoy" : "Reservas del día"}</span>
            <strong>{day ? day.reservations_today : "—"}</strong>
            <small>{day ? `de ${day.slots_today} turnos · ${occupancy}%` : "Cargando…"}</small>
          </div>
          <button type="button" className="club-panel__tile" onClick={() => onGoTo("socios")}>
            <span>Solicitudes por aprobar</span>
            <strong>{pendingRequests.length}</strong>
            <small>Ver en Socios ›</small>
          </button>
          <div className="club-panel__tile">
            <span>Socios activos</span>
            <strong>{day ? day.members_active : "—"}</strong>
            <small>{suspended > 0 ? `${suspended} suspendidos` : "Sin suspendidos"}</small>
          </div>
        </div>
      </section>

      {error && <p className="form-error" role="alert">{error}</p>}

      <section className="club-panel__card club-day" aria-label="Canchas del día">
        <div className="club-day__head">
          <div className="club-day__head-text">
            <h2>Canchas · {isToday ? `Hoy, ${SHORT_DATE.format(dateFromIso(dateIso))}` : dayWord}</h2>
            <p className="club-panel__muted club-day__desktop-only">
              Toca un hueco libre para reservar a nombre de un socio. Toca una reserva para verla o cancelarla.
            </p>
          </div>
          <div className="club-day__filters club-day__desktop-only">
            <select
              className="t123-select"
              aria-label="Deporte"
              value={sportFilter}
              onChange={(e) => {
                setSportFilter(e.target.value);
                setCourtFilter("");
              }}
            >
              <option value="">Todos los deportes</option>
              {sports.map((s) => (
                <option key={s} value={s}>{sportLabel(s)}</option>
              ))}
            </select>
            <select
              className="t123-select"
              aria-label="Cancha"
              value={courtFilter}
              onChange={(e) => setCourtFilter(e.target.value)}
            >
              <option value="">Todas las canchas</option>
              {courtOptions.map((c) => (
                <option key={c.id} value={String(c.id)}>{c.name}</option>
              ))}
            </select>
          </div>
        </div>
        {dayChips}
        <div className="club-day__mobile-only club-day__seg" role="group" aria-label="Vista">
          <button type="button" aria-pressed={mobileView === "court"} onClick={() => setMobileView("court")}>
            Por cancha
          </button>
          <button type="button" aria-pressed={mobileView === "all"} onClick={() => setMobileView("all")}>
            Todas las canchas
          </button>
        </div>
        <div className="club-day__desktop-only">{legend}</div>
        {day && openCourts.length === 0 && (
          <p className="club-panel__muted">
            Ese día no hay canchas abiertas. Configura horarios en «Canchas y horarios».
          </p>
        )}
      </section>

      {/* Escritorio: cuadrícula con columna de horas y encabezados fijos. */}
      {window_ && visibleCourts.length > 0 && (
        <section className="club-panel__card club-day__grid-card club-day__desktop-only" aria-label="Cuadrícula de canchas">
          <div className="club-day__scroll" data-testid="club-day-grid">
            <div className="club-day__grid" style={{ minWidth: 64 + visibleCourts.length * 150 }}>
              <div className="club-day__header-row">
                <div className="club-day__corner" />
                {visibleCourts.map((court) => {
                  const occ = courtOccupancy(court);
                  return (
                    <div className="club-day__court-head" key={court.id}>
                      <div className="club-day__court-title">
                        <strong>{court.name}</strong>
                        <span className={`club-day__sport ${sportClass(court.sport)}`}>
                          {sportLabel(court.sport || "tenis")} · {court.slot_minutes ?? "—"} min
                        </span>
                      </div>
                      <div className="club-day__occ">
                        <div className="club-day__occ-bar" aria-hidden="true">
                          <div style={{ width: `${occ.percent}%` }} />
                        </div>
                        <small>{occ.percent}% · {occ.taken} de {occ.total}</small>
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="club-day__body" style={{ height: (window_.endMin - window_.startMin) * PX_PER_MIN + 8 }}>
                <div className="club-day__times">
                  {Array.from(
                    { length: (window_.endMin - window_.startMin) / 60 + 1 },
                    (_, i) => window_.startMin + i * 60,
                  )
                    // La etiqueta roja de «ahora» ocupa ese lugar: no se enciman.
                    .filter((m) => nowLine === null || Math.abs((m - window_.startMin) * PX_PER_MIN - nowLine) > 28)
                    .map((m) => (
                      <span key={m} style={{ top: (m - window_.startMin) * PX_PER_MIN }}>{fromMinutes(m)}</span>
                    ))}
                </div>
                {visibleCourts.map((court) => (
                  <div className="club-day__col" key={court.id}>
                    {courtBlocks(court).map((block) => {
                      const past = isPastBlock(block, dateIso, todayIso, nowMin);
                      const style = {
                        top: (block.startMin - window_.startMin) * PX_PER_MIN + 4,
                        height: (block.endMin - block.startMin) * PX_PER_MIN - 4,
                      };
                      // Turnos cortos (≤ 60 min): el nombre en un renglón para que quepa el detalle.
                      const short = block.endMin - block.startMin <= 60 ? " is-short" : "";
                      const className = `club-day__block club-day__block--${block.kind}${past ? " is-past" : ""}${short}`;
                      if (block.kind === "free") {
                        if (past) {
                          return (
                            <div key={block.start} className={className} style={style} data-testid="club-day-block">
                              <span className="club-day__free-idle">{block.timeLabel}</span>
                            </div>
                          );
                        }
                        return (
                          <Link
                            key={block.start}
                            className={className}
                            style={style}
                            to={reserveHref(court, block.start)}
                            aria-label={`Reservar ${court.name} ${block.timeLabel}`}
                            data-testid="club-day-block"
                          >
                            {renderBlockBody(block)}
                          </Link>
                        );
                      }
                      if (block.kind === "reserved" || block.kind === "pending") {
                        return (
                          <Link
                            key={block.start}
                            className={className}
                            style={style}
                            to={viewHref()}
                            title={`${block.label ?? ""} · ${block.timeLabel} · ${blockSubtitle(block)}`}
                            data-testid="club-day-block"
                          >
                            {renderBlockBody(block, false, Boolean(short))}
                          </Link>
                        );
                      }
                      return (
                        <div
                          key={block.start}
                          className={className}
                          style={style}
                          title={`${blockTitle(block)} · ${block.timeLabel} · ${blockSubtitle(block)}`}
                          data-testid="club-day-block"
                        >
                          {renderBlockBody(block, false, Boolean(short))}
                        </div>
                      );
                    })}
                  </div>
                ))}
                {nowLine !== null && (
                  <>
                    <div className="club-day__now" style={{ top: nowLine }} aria-hidden="true" />
                    <span className="club-day__now-tag" style={{ top: nowLine - 9 }}>{fromMinutes(nowMin)}</span>
                  </>
                )}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Celular: primero la cancha (con su %) y la lista vertical de turnos. */}
      {openCourts.length > 0 && mobileView === "court" && mobileCourt && (
        <section className="club-panel__card club-day__mobile-only" aria-label="Turnos por cancha">
          <div className="club-day__court-cards" role="group" aria-label="Elegir cancha">
            {openCourts.map((court) => (
              <button
                key={court.id}
                type="button"
                className="club-day__chip club-day__court-card"
                aria-pressed={court.id === mobileCourt.id}
                onClick={() => setMobileCourtId(court.id)}
              >
                <small>{sportLabel(court.sport || "tenis")}</small>
                <strong>{court.name}</strong>
                <small>{courtOccupancy(court).percent}% ocupada</small>
              </button>
            ))}
          </div>
          <div className="club-day__court-line">
            <strong>{mobileCourt.name}</strong>
            <span className={`club-day__sport ${sportClass(mobileCourt.sport)}`}>
              {sportLabel(mobileCourt.sport || "tenis")} · turnos de {mobileCourt.slot_minutes ?? "—"} min
            </span>
          </div>
          {legend}
          <div className="club-day__list">
            {(() => {
              const blocks = courtBlocks(mobileCourt);
              const nowIndex = isToday ? blocks.findIndex((b) => b.endMin > nowMin) : -1;
              return blocks.map((block, i) => {
                const past = isPastBlock(block, dateIso, todayIso, nowMin);
                const row = (
                  <div key={block.start} data-tap className={`club-day__row club-day__block--${block.kind}${past ? " is-past" : ""}`}>
                    <div className="club-day__row-time">
                      <strong>{block.timeLabel.split("–")[0]}</strong>
                      <small>{block.timeLabel.split("–")[1]}</small>
                    </div>
                    {block.kind === "free" ? (
                      <>
                        <span className="club-day__row-free">Libre</span>
                        {!past && (
                          <Link className="btn btn--compact club-day__reserve-btn" to={reserveHref(mobileCourt, block.start)}>
                            + Reservar
                          </Link>
                        )}
                      </>
                    ) : (
                      <div className="club-day__row-main">
                        <strong>{blockTitle(block)}</strong>
                        <span>{blockSubtitle(block)}</span>
                      </div>
                    )}
                  </div>
                );
                if (i === nowIndex && nowIndex > 0) {
                  return [
                    <div className="club-day__now-sep" key="now">
                      <span>Ahora {fromMinutes(nowMin)}</span>
                    </div>,
                    row,
                  ];
                }
                return row;
              });
            })()}
          </div>
        </section>
      )}

      {openCourts.length > 0 && mobileView === "all" && (() => {
        const win = dayWindow(openCourts);
        if (!win) return null;
        const height = (win.endMin - win.startMin) * PX_PER_MIN_COMPACT + 4;
        return (
          <section className="club-panel__card club-day__mobile-only" aria-label="Todas las canchas">
            <div className="club-day__mini-head">
              <span className="club-day__mini-gutter" />
              {openCourts.map((c) => (
                <span key={c.id} className="club-day__mini-name">{c.name}</span>
              ))}
            </div>
            <div className="club-day__mini" style={{ height }}>
              <div className="club-day__mini-gutter club-day__mini-times">
                {Array.from({ length: (win.endMin - win.startMin) / 120 + 1 }, (_, i) => win.startMin + i * 120).map((m) => (
                  <span key={m} style={{ top: (m - win.startMin) * PX_PER_MIN_COMPACT }}>{fromMinutes(m).slice(0, 2)}h</span>
                ))}
              </div>
              {openCourts.map((court) => (
                <div className="club-day__mini-col" key={court.id}>
                  {courtBlocks(court)
                    .filter((b) => b.kind !== "free")
                    .map((block) => (
                      <button
                        key={block.start}
                        type="button"
                        className={`club-day__mini-block club-day__block--${block.kind}${isPastBlock(block, dateIso, todayIso, nowMin) ? " is-past" : ""}`}
                        style={{
                          top: (block.startMin - win.startMin) * PX_PER_MIN_COMPACT + 2,
                          height: (block.endMin - block.startMin) * PX_PER_MIN_COMPACT - 2,
                        }}
                        aria-label={`${court.name} ${block.timeLabel} ${blockTitle(block)}`}
                        onClick={() => {
                          setMobileCourtId(court.id);
                          setMobileView("court");
                        }}
                      />
                    ))}
                </div>
              ))}
              {isToday && nowMin >= win.startMin && nowMin <= win.endMin && (
                <div className="club-day__now club-day__now--mini" style={{ top: (nowMin - win.startMin) * PX_PER_MIN_COMPACT }} />
              )}
            </div>
            {legend}
          </section>
        );
      })()}

      <div className="club-panel__split">
        <section className="club-panel__card" aria-label="Solicitudes para unirse">
          <div className="club-panel__card-head">
            <h2>Solicitudes para unirse · {pendingRequests.length}</h2>
            {pendingRequests.length > 3 && (
              <button type="button" className="btn btn--compact btn--ghost" onClick={() => onGoTo("socios")}>
                Ver todas
              </button>
            )}
          </div>
          {pendingRequests.length === 0 ? (
            <p className="club-panel__muted">No hay solicitudes pendientes.</p>
          ) : (
            <div className="club-list">
              {pendingRequests.slice(0, 3).map((request) => (
                <div className="club-list__item" key={request.id}>
                  <span>
                    <strong>{requesterName(request)}</strong>
                    <small>
                      {formatDate(request.created_at, "Sin fecha")} ·{" "}
                      {request.source === "invite" ? "con link de invitación" : "por la app"}
                    </small>
                  </span>
                  <span className="club-list__actions">
                    <button
                      type="button"
                      className="btn btn--compact"
                      disabled={saving}
                      onClick={() => onMembershipDecision(request.id, "approve")}
                    >
                      Aprobar
                    </button>
                    <button
                      type="button"
                      className="btn btn--compact btn--ghost"
                      disabled={saving}
                      onClick={() => onMembershipDecision(request.id, "reject")}
                    >
                      Rechazar
                    </button>
                  </span>
                </div>
              ))}
            </div>
          )}
        </section>

        <section className="club-panel__card" aria-label="Membresía Torneos123">
          <div className="club-panel__card-head"><h2>Membresía Torneos123 de tus socios</h2></div>
          {/* Sin cobro todavía: no hay «al corriente / en gracia» reales que mostrar. */}
          <div className="club-membership__counts">
            <div>
              <strong>{day ? byStatus.active ?? 0 : "—"}</strong>
              <span className="club-panel__muted">Activos</span>
            </div>
            <div>
              <strong>{day ? suspended : "—"}</strong>
              <span className="club-panel__muted">Suspendidos</span>
            </div>
            <div>
              <strong>{pendingRequests.length}</strong>
              <span className="club-panel__muted">Por aprobar</span>
            </div>
          </div>
          <ClubT123MembershipTile
            token={token}
            reloadKey={reloadKey}
            embedded
            fallback={
              <>
                <span className="club-membership__soon">Cobro de membresía: disponible pronto</span>
                <p className="club-panel__muted">
                  El cobro automático se activa en la siguiente etapa. Tu cuota del club la manejas por fuera.
                </p>
              </>
            }
          />
        </section>
      </div>
    </>
  );
}
